import { observer } from 'mobx-react-lite'
import React, { useMemo } from 'react'

// evertel
import { Dropdown, DropdownSelectProps } from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { DepartmentStore } from '@evertel/department'
import { DocumentSearchFilter } from '@evertel/document'
import { APIDataDocumentSchema } from '@evertel/types'
import { getAbbreviatedName } from '@evertel/utils'

interface DocumentSchemaDropdownProps extends Omit<DropdownSelectProps, 'options'> {
    filter: DocumentSearchFilter
    onFilterChange: (updates: Partial<DocumentSearchFilter>) => void
    documentSchemas: APIDataDocumentSchema[]
    placeholder?: string
}

interface SchemaOption {
    name: string
    extra?: string
    label: string
    value: number
}

const DocumentSchemaSearchSelect: React.FC<DocumentSchemaDropdownProps> = observer(({
    filter,
    onFilterChange,
    documentSchemas,
    placeholder = 'Select...',
    ...otherProps
}) => {

    const departmentStore = useService(DepartmentStore, [])

    const schemaOptions = useMemo((): SchemaOption[] => {
        if (!documentSchemas?.length) {
            return []
        }

        return documentSchemas.map((schema) => {
            const department = departmentStore.findById(schema.departmentId as number)
            const shortName = department?.shortName?.trim() || getAbbreviatedName(department?.name)
            const extra = (filter?.by?.type === 'department') ? '' : shortName

            return {
                name: schema.name,
                extra: extra,
                label: schema?.name + (extra ? ` (${extra})` : ''),
                value: schema.id
            }
        })
    }, [documentSchemas, departmentStore, filter?.by?.type])

    const formatOptionLabel = (data: any) => {
        let label = data.name ?? data.label
        if (data.extra) {
            label = label + ` <span class="text-muted">(${data.extra})</span>`
        }
        return (
            <span dangerouslySetInnerHTML={{ __html: label }} />
        )
    }

    const handleChange = (option: any) => {
        onFilterChange({ schemaId: option?.value ?? null })
    }

    return (
        <Dropdown
            value={filter?.schemaId}
            options={schemaOptions}
            formatOptionLabel={formatOptionLabel}
            onChange={handleChange}
            placeholder={placeholder}
            {...otherProps}
        />
    )
})

export {
    DocumentSchemaSearchSelect
}
